import { useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { DemanderKbn } from "../../assets/data/Constants";
import TkStepper from "../../components/atoms/TkStepper";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { IAPI_RET005_Request } from "../../type/apiType";
import { httpAPI_RET005 } from "./api/request.formdata";
import { fakeTableList, isCheckedSchema, schema, stepper } from "./parts";
import { API_RET005_BuildReqParams } from "../../utils/buildRequestParams";
import { businessCheck } from "./hooks/useHooks";
import { messageids } from "../../assets/data/TkMessageProperty";
import _ from "lodash";

/**
 * SCRR411107 退職手当金の請求手続き（振込先金融機関の登録）
 * 退職手当金請求者が退職手当金の振込先金融機関の情報を登録する。
 * @return SCRR411107
 */
const SCRR411107 = () => {
  const navigate = useNavigate();

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const isUserTypeKiko =
    state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO;
  const context = businessState.job_35;

  const [stateTableList, setStateTableList] = useState<any>([]);
  const [bankInfo, setBankInfo] = useState<any>({});
  const [isSendMoneyAbroad, setIsSendMoneyAbroad] = useState(false);
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const [warning, setWarning] = useState(false);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const isRetiree = context?.demanderKbn === DemanderKbn["退職者本人"];

  // 初期表示
  useEffect(() => {
    let resArr = stateFakeTableList.current;

    for (let i in context) {
      let targetObj = resArr.find((item) => item.keyword === i)!;

      if (i === "depositType") continue;

      if (targetObj && (context as any)[i]) {
        targetObj.text = (context as any)[i];
        targetObj.setDefaultValue = (context as any)[i];
      }
    }

    let retireeName = resArr.find((item) => item.keyword === "retireeName")!;
    let bankSelect = resArr.find((item) => item.keyword === "bankSelect")!;

    // 退職者名の表示
    retireeName.contractorNumber = context?.staffNameKanji;
    retireeName.showLine = isUserTypeKiko;

    // 振込先金融機関
    let bankInfo = {
      bankCode: context?.bankCd,
      bankNameKatakana: context?.bankName,
      branchCode: context?.branchCd,
      branchNameKatakana: context?.branchName,
    };
    if (Object.values(bankInfo).every((i) => i)) {
      bankSelect.setDefaultValue = bankInfo;
      setBankInfo(bankInfo);
    }
    bankSelect.isDaikou = isUserTypeKiko;
    setIsSendMoneyAbroad(context?.sendMoneyAbroad === "1");

    setStateTableList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resArr = [...stateFakeTableList.current];
    let bankSelection = resArr.find((item) => item.keyword === "bankSelect")!;
    let formArea: any = resArr.filter((_item, index) => index > 2)!;
    let accountNameKana = resArr.find(
      (item) => item.keyword === "accountNameKana"
    )!;

    // 金融機関検索
    bankSelection.onSuccessfulWithBank = (value: any) => {
      resArr.find((item) => item.keyword === "bankCd")!.text = value.bankCode;
      resArr.find((item) => item.keyword === "bankName")!.text =
        value.bankNameKatakana;
      resArr.find((item) => item.keyword === "branchCd")!.text =
        value.branchCode;
      resArr.find((item) => item.keyword === "branchName")!.text =
        value.branchNameKatakana;

      setBankInfo(value);
    };

    // 海外送金
    bankSelection.onGoToKaiGai = (e: any) => {
      bankSelection.checkKaigai = e;
      setIsSendMoneyAbroad(e);

      formArea.forEach((item: any) => {
        if (item.keyword !== "depositType") {
          item.text = "";
          item.setDefaultValue = "";
        }
      });
      setBankInfo({});
    };

    accountNameKana.onGetInputValue = (value: any) => {
      setName((value.lastName ?? "") + " " + (value.firstName ?? ""));
    };

    setStateTableList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    let resArr = [...stateFakeTableList.current];
    let bankSelect = resArr.find((item) => item.keyword === "bankSelect")!;
    let domesticResidenceFlg = resArr.find(
      (item) => item.keyword === "domesticResidenceFlg"
    )!;
    let formArea: any = resArr.filter((_item, index) => index > 3)!;

    bankSelect.checkKaigai = isSendMoneyAbroad;

    domesticResidenceFlg.showLine = isSendMoneyAbroad;
    formArea.forEach((item: any) => {
      item.showLine = !isSendMoneyAbroad;
    });

    setStateTableList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSendMoneyAbroad]);

  const makeHttpObj = (obj: any): IAPI_RET005_Request => {
    const reqObj: IAPI_RET005_Request = {
      ...context,
      fileList: context?.fileList ?? [],
      ...obj,
      tempSaveStep: 4,
      businessType: businessState.common?.businessType,
      contractorNo: isUserTypeKiko
        ? businessState.common?.contractorNo
        : state.authInfo?.contractorNo,
      nowZipcode: isRetiree
        ? context?.demanderZipcode
        : context?.retirementZipcode,
      nowPrefectureCd: isRetiree
        ? context?.demanderPrefectureCd
        : context?.retirementPrefectureCd,
      nowAddressCity: isRetiree
        ? context?.demanderCity
        : context?.retirementAddressCity,
      nowAddressOthers: isRetiree
        ? context?.demanderAddressOthers
        : context?.retirementAddressOthers,
      contractorName: businessState.common?.contractorName,
      screenId: "SCRR411107",
    };

    return API_RET005_BuildReqParams(reqObj);
  };

  const handleSubmit = async (data: any, index: any) => {
    const dataObj = {
      domesticResidenceFlg: isSendMoneyAbroad
        ? data.domesticResidenceFlg
        : undefined,
      bankCd: bankInfo.bankCode,
      bankName: bankInfo.bankNameKatakana,
      branchCd: bankInfo.branchCode,
      branchName: bankInfo.branchNameKatakana,
      depositType: isSendMoneyAbroad ? undefined : data.depositType,
      accountNo: isSendMoneyAbroad ? undefined : data.accountNo,
      accountNameKana: isSendMoneyAbroad ? undefined : name,
      sendMoneyAbroad: isSendMoneyAbroad ? "1" : "0",
    };

    switch (index) {
      case 0: // 一時保存
        // 退職手当金請求更新API実行
        try {
          const res = await httpAPI_RET005({
            ...makeHttpObj(dataObj),
          });

          if (res.resultCd === "I0000") {
            setBusinessState({
              ...businessState,
              job_35: {
                ...context,
                ...dataObj,
                tempSaveStep: 4,
                applyId: res.applyId,
              },
            });
            setState({ ...state, errorMsgId: "" });
            setOpen(true);
          } else {
            setState({
              ...state,
              errorMsgId: res.resultCd,
            });
          }
        } catch (error) {
          navigate("/error");
        }
        break;

      case 1: // 次へ
        setState({ ...state, errorMsgId: "" });

        setBusinessState({
          ...businessState,
          job_35: {
            ...context,
            ...dataObj,
          },
        });

        // 業務チェック
        if (!isSendMoneyAbroad && context?.demanderNameKana) {
          let error = businessCheck(context.demanderNameKana, name);

          if (error === "error") {
            setState({ ...state, errorMsgId: "TKMSG_SCR0072E" });
            return;
          } else if (error === "warning") {
            setWarning(true);
            return;
          }
        }

        onSuccess();
        break;
    }
  };

  const onSuccess = () => {
    if (context?.demanderKbn === DemanderKbn["退職者本人"]) {
      navigate("/SCRR411109");
    } else {
      navigate("/SCRR411108");
    }
  };

  return (
    <Box data-testid="SCRR411107test">
      <TkStepper
        steps={stepper}
        activeStep={3}
        savaStep={context?.tempSaveStep}
      />

      <TkFakeTable
        list={stateTableList}
        ruleSchema={isSendMoneyAbroad ? isCheckedSchema : schema}
        submitButtonGroup={[
          { submitButtonText: "一時保存" },
          { submitButtonText: "次へ" },
        ]}
        buttonGroup={<TkBackButton />}
        onCheckAndSubmit={(e, index) => handleSubmit(e, index)}
      />

      <TkMessageDialog
        open={open}
        onOky={() => setOpen(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />

      <TkMessageDialog
        open={warning}
        onOky={onSuccess}
        onCancel={() => setWarning(false)}
        title="警告"
        contextText={messageids.messageid.TKMSG_SCR0008W}
        okText="確認"
        cancelText="戻る"
      />
    </Box>
  );
};

export default SCRR411107;
